/**
 * <gallery-wall-customizer> — render a gallery-wall layout and let the customer
 * upload one photo per frame (drag & drop or browse), shown either on a plain
 * wall or in a room photo.
 *
 * Geometry comes from assets/gallery-wall-layouts.json and the room placement
 * from assets/gallery-wall-rooms.json (both ported from the GalleryWalls
 * generator); the frame/mat/artwork styling is assets/gallery-wall.css.
 *
 * Attributes:
 *   data-layout            layout name (key in gallery-wall-layouts.json)
 *   data-layouts           url of the layouts json (asset_url)
 *   data-room              room name (key in gallery-wall-rooms.json)
 *   data-rooms             url of the rooms json (asset_url)
 *   data-upload-endpoint   base url of the uploads worker; when set, each photo
 *                          is uploaded and its URL goes on the line item
 *   data-enable-properties "true" to mirror the chosen filenames into the
 *                          product form as line-item properties (preview-only)
 *
 * The room picker also offers the customer's own background photo. It stays
 * local (never uploaded), drags to position, and asks for the wall width so the
 * fixed gallery scales against a number the customer can measure. Any loadable
 * image is accepted; the preview is not printed.
 *
 * With an upload endpoint configured, `properties[Artwork N]` carries the
 * uploaded file's URL and checkout is blocked while any upload is in flight or
 * has failed. Without one, it stays preview-only (filenames, opt-in).
 */
/**
 * The add-to-cart form. A product page carries several `form[action*="/cart/add"]`
 * elements (Shop Pay installments, etc.); only the one with the add button
 * actually submits, so line-item properties must be written there.
 */
function cartForm() {
  const forms = [...document.querySelectorAll('form[action*="/cart/add"]')];
  return forms.find((f) => f.querySelector('button[name="add"]')) || forms[0] || null;
}

if (!customElements.get('gallery-wall-customizer')) {
  customElements.define(
    'gallery-wall-customizer',
    class GalleryWallCustomizer extends HTMLElement {
      connectedCallback() {
        this.wall = this.querySelector('[data-wall]');
        this.controls = this.querySelector('[data-controls]');
        this.stage = this.querySelector('[data-stage]');
        this.roomPicker = this.querySelector('[data-room-picker]');
        this.fileInput = this.querySelector('[data-file-input]');
        this.roomFileInput = this.querySelector('[data-room-file-input]');
        this.customScale = this.querySelector('[data-custom-scale]');
        this.customScaleInput = this.querySelector('[data-custom-scale-input]');
        this.customScaleValue = this.querySelector('[data-custom-scale-value]');
        this.customReplace = this.querySelector('[data-custom-replace]');
        this.customControls = this.querySelector('[data-custom-controls]');
        this.roomNote = this.querySelector('[data-room-note]');
        this.uploadEndpoint = (this.dataset.uploadEndpoint || '').replace(/\/$/, '');
        this.requirePhoto = this.dataset.requirePhoto === 'true';
        this.wallKey = 'wall'; // the plain wall, offered alongside the room photos
        this.customKey = 'custom'; // the customer's own background photo
        this.customRoom = null;
        this.customWallWidth = 10;
        this.customWidthPct = 45;
        this.view = 'room';
        this.files = new Map();
        this.activeIndex = null;
        this.offset = { x: 0, y: 0 }; // preview-only drag offset, % of the room
        this.roomBase = null;
        this.dragState = null;
        this.lastDragEnd = 0;

        this.noteEl = this.querySelector('[data-note]');
        this.noteEl?.addEventListener('input', () => this.syncNote());

        this.fileInput.addEventListener('change', () => this.setFile(this.activeIndex, this.fileInput.files[0]));
        this.roomFileInput?.addEventListener('change', () => this.setCustomRoom(this.roomFileInput.files[0]));
        this.customScaleInput?.addEventListener('input', () =>
          this.setCustomWallWidth(this.customScaleInput.value)
        );

        for (const type of ['pointerdown', 'pointermove', 'pointerup', 'pointercancel']) {
          this.stage?.addEventListener(type, (event) => this.onPointer(type, event));
        }

        this.addEventListener('click', (event) => {
          // a drag that just ended shouldn't also fire the frame's upload picker
          if (Date.now() - this.lastDragEnd < 350) {
            event.preventDefault();
            event.stopPropagation();
            return;
          }
          const reset = event.target.closest('[data-reset-position]');
          if (reset) {
            this.resetPosition();
            return;
          }
          const replace = event.target.closest('[data-custom-replace]');
          if (replace) {
            this.openCustomRoomPicker();
            return;
          }
          const clear = event.target.closest('[data-clear]');
          if (clear) {
            event.preventDefault();
            this.clear(clear.dataset.clear);
            return;
          }
          const upload = event.target.closest('[data-upload]');
          if (upload) this.pick(upload.dataset.upload);
          const room = event.target.closest('[data-room-value]');
          if (room) this.setRoom(room.dataset.roomValue);
        });

        this.addEventListener('dragover', (event) => {
          const zone = event.target.closest('[data-dropzone]');
          if (!zone) return;
          event.preventDefault();
          zone.classList.add('is-dragover');
        });
        this.addEventListener('dragleave', (event) => {
          event.target.closest('[data-dropzone]')?.classList.remove('is-dragover');
        });
        this.addEventListener('drop', (event) => {
          const zone = event.target.closest('[data-dropzone]');
          if (!zone) return;
          event.preventDefault();
          zone.classList.remove('is-dragover');
          const file = event.dataTransfer?.files?.[0];
          if (file && file.type.startsWith('image/')) this.setFile(Number(zone.dataset.dropzone), file);
        });

        this.gateSubmit();
        this.load();
      }

      async load() {
        try {
          const [layouts, rooms] = await Promise.all([
            fetch(this.dataset.layouts).then((r) => r.json()),
            fetch(this.dataset.rooms).then((r) => r.json()),
          ]);
          this.layout = layouts[this.dataset.layout];
          this.rooms = rooms;
          this.roomName = this.dataset.room;
          this.room = rooms[this.roomName];
        } catch (error) {
          console.error('gallery-wall-customizer: could not load layout data', error);
        }
        if (!this.layout) {
          this.wall.textContent = `Unknown layout “${this.dataset.layout}”`;
          return;
        }
        this.render();
      }

      render() {
        const { frames } = this.layout;
        this.wall.style.setProperty('--gw-w', this.layout.canvas.w);
        this.wall.style.setProperty('--gw-h', this.layout.canvas.h);
        this.wall.style.setProperty('--frame-border', this.layout.border);

        this.wall.innerHTML = frames
          .map(
            (frame, index) => `
            <figure class="frame" data-frame-index="${index}"
              style="--x:${frame.x}; --y:${frame.y}; --w:${frame.w}; --h:${frame.h}; --mat:${frame.mat}">
              <div class="frame__mat"><div class="frame__art" data-art="${index}"></div></div>
              <span class="gallery-wall-customizer__frame-num" aria-hidden="true">${index + 1}</span>
              <button type="button" class="gallery-wall-customizer__hit" data-upload="${index}"
                aria-label="Choose a photo for frame ${index + 1}">
                <span class="gallery-wall-customizer__plus" aria-hidden="true">+</span>
              </button>
            </figure>`
          )
          .join('');

        this.controls.innerHTML =
          `<p class="gallery-wall-customizer__notice" data-notice hidden></p>` +
          frames
            .map(
              (frame, index) => `
            <div class="gallery-wall-customizer__frame" data-row="${index}">
              <span class="gallery-wall-customizer__label">
                Frame ${index + 1}
                <span class="gallery-wall-customizer__size">${frame.w}×${frame.h} in</span>
              </span>
              <div class="gallery-wall-customizer__dropzone" data-dropzone="${index}" data-upload="${index}"
                role="button" tabindex="0" aria-label="Choose a photo for frame ${index + 1}">
                <img class="gallery-wall-customizer__thumb" data-thumb="${index}" alt="" hidden>
                <span class="gallery-wall-customizer__prompt" data-prompt="${index}">
                  Drag &amp; drop your file or <span class="gallery-wall-customizer__browse">Browse</span>
                </span>
                <span class="gallery-wall-customizer__file" data-filename="${index}"></span>
                <button type="button" class="gallery-wall-customizer__clear" data-clear="${index}" hidden
                  aria-label="Remove the photo for frame ${index + 1}">&times;</button>
              </div>
            </div>`
            )
            .join('');

        this.renderRoomPicker();
        // Room view is the default: the wall shown in a room reads as the
        // finished piece, where a bare wall reads as a layout diagram. The view
        // has to be set before placing, because placing is room-only.
        this.setView(this.dataset.defaultView || 'room');
        this.placeInRoom();
      }

      setView(view) {
        this.view = view;
        const isRoom = view === 'room';
        this.stage?.classList.toggle('is-room', isRoom);
        this.stage?.classList.toggle('is-custom-room', isRoom && Boolean(this.room?.custom));
        this.classList.toggle('is-room-view', isRoom);
        this.updateRoomPicker();
        this.syncCustomScale();
        if (isRoom) this.placeInRoom();
      }

      /**
       * One row of choices: each room photo, the customer's own background,
       * then the plain wall. The wall is what the customizer used to show
       * behind a separate "On the wall" tab.
       */
      renderRoomPicker() {
        if (!this.roomPicker || !this.rooms) return;
        const options = Object.keys(this.rooms)
          .filter((key) => !key.startsWith('_'))
          .map((key) => ({ key, label: this.rooms[key].label || key }));
        options.push({ key: this.customKey, label: 'Your room' });
        options.push({ key: this.wallKey, label: 'Wall' });

        this.roomPicker.innerHTML = options
          .map(
            ({ key, label }) => `<button type="button" class="gallery-wall-customizer__room"
              data-room-value="${key}"${
                key === this.customKey ? ' title="Upload a photo of your room"' : ''
              }>${label}</button>`
          )
          .join('');
        this.updateRoomPicker();
      }

      updateRoomPicker() {
        if (!this.roomPicker) return;
        this.roomPicker.querySelectorAll('[data-room-value]').forEach((button) => {
          const value = button.dataset.roomValue;
          const active =
            value === this.wallKey ? this.view !== 'room' : this.view === 'room' && value === this.roomName;
          button.classList.toggle('is-active', active);
        });
      }

      setRoom(key) {
        this.hideRoomNote();
        if (key === this.wallKey) {
          this.setView('wall');
          return;
        }
        if (key === this.customKey) {
          if (!this.customRoom) {
            this.openCustomRoomPicker();
            return;
          }
          this.roomName = key;
          this.room = this.customRoom;
          this.offset = { x: 0, y: 0 };
          this.setView('room');
          return;
        }
        if (!this.rooms || !this.rooms[key]) return;
        this.roomName = key;
        this.room = this.rooms[key];
        this.offset = { x: 0, y: 0 };
        this.setView('room');
      }

      openCustomRoomPicker() {
        if (!this.roomFileInput) return;
        this.roomFileInput.value = '';
        this.roomFileInput.click();
      }

      /**
       * Validate and show the customer's own background photo. It is preview
       * only: the file never leaves the browser, so it is not an order asset.
       * Any loadable image is accepted; the stage takes the photo's aspect
       * ratio, so portrait and landscape both work.
       */
      async setCustomRoom(file) {
        if (!file) return;

        if (!file.type.startsWith('image/')) {
          this.showRoomNote('Use a JPG, PNG or WebP photo for your room.');
          return;
        }
        if (file.size > 15 * 1024 * 1024) {
          this.showRoomNote('That photo is larger than 15 MB. Choose a smaller file.');
          return;
        }

        const url = URL.createObjectURL(file);
        let image;
        try {
          image = await this.loadImage(url);
        } catch (error) {
          URL.revokeObjectURL(url);
          this.showRoomNote('That photo could not be opened. Try a JPG or PNG.');
          return;
        }

        if (this.customRoom?.image) URL.revokeObjectURL(this.customRoom.image);
        this.customRoom = {
          image: url,
          size: { w: image.naturalWidth, h: image.naturalHeight },
          label: 'Your room',
          custom: true,
        };

        this.roomName = this.customKey;
        this.room = this.customRoom;
        this.customWallWidth = this.defaultCustomWallWidth();
        this.customWidthPct = this.wallWidthPct(this.customWallWidth);
        this.offset = { x: 0, y: 0 };
        this.hideRoomNote();
        this.setView('room');
      }

      loadImage(url) {
        return new Promise((resolve, reject) => {
          const image = new Image();
          image.onload = () => resolve(image);
          image.onerror = reject;
          image.src = url;
        });
      }

      showRoomNote(message) {
        if (!this.roomNote) return;
        this.roomNote.textContent = message;
        this.roomNote.hidden = false;
      }

      hideRoomNote() {
        if (this.roomNote) this.roomNote.hidden = true;
      }

      /** The wall's natural aspect ratio, used to size it over a custom photo. */
      wallAspect() {
        return this.layout ? this.layout.canvas.w / this.layout.canvas.h : 1;
      }

      /** Widest wall that still leaves the picture readable, as a % of its width. */
      customMaxWidth() {
        if (!this.room?.custom) return 90;
        const imageAspect = this.room.size.w / this.room.size.h;
        return Math.min(90, (92 * this.wallAspect()) / imageAspect);
      }

      /**
       * The wall width, in feet, implied by the photo. The slider sets how many
       * feet the whole photo spans, so the fixed 53 in gallery scales against a
       * number the customer can actually measure. Bounds keep the wall inside
       * the image at both ends.
       */
      wallWidthRange() {
        if (!this.room?.custom || !this.layout) return { min: 6, max: 30 };
        const min = Math.ceil((this.layout.canvas.w / (this.customMaxWidth() / 100) / 12) * 2) / 2;
        const max = Math.floor((this.layout.canvas.w / 0.15 / 12) * 2) / 2;
        return { min, max: Math.max(min + 0.5, max) };
      }

      wallWidthPct(feet) {
        return ((this.layout.canvas.w / (feet * 12)) * 100);
      }

      defaultCustomWallWidth() {
        const { min, max } = this.wallWidthRange();
        return Math.min(Math.max(10, min), max);
      }

      formatWallWidth(feet) {
        const value = Math.round(feet * 2) / 2;
        return `${Number.isInteger(value) ? value : value.toFixed(1)} ft`;
      }

      syncCustomScale() {
        const isCustom = this.view === 'room' && Boolean(this.room?.custom);
        this.customControls?.toggleAttribute('hidden', !isCustom);
        this.customScale?.toggleAttribute('hidden', !isCustom);
        this.customReplace?.toggleAttribute('hidden', !isCustom);
        if (!isCustom || !this.customScaleInput) return;

        const { min, max } = this.wallWidthRange();
        this.customWallWidth = Math.min(Math.max(this.customWallWidth, min), max);
        this.customScaleInput.min = String(min);
        this.customScaleInput.max = String(max);
        this.customScaleInput.value = String(this.customWallWidth);
        if (this.customScaleValue) this.customScaleValue.textContent = this.formatWallWidth(this.customWallWidth);
      }

      setCustomWallWidth(value) {
        if (!this.room?.custom || !this.roomBase) return;

        const { min, max } = this.wallWidthRange();
        const feet = Math.min(Math.max(Number(value), min), max);
        const widthPct = Math.min(this.wallWidthPct(feet), this.customMaxWidth());
        const heightPct = (widthPct * (this.room.size.w / this.room.size.h)) / this.wallAspect();
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        const centerX = this.roomBase.leftPct + this.roomBase.widthPct / 2 + this.offset.x;
        const centerY = this.roomBase.topPct + this.roomBase.heightPct / 2 + this.offset.y;

        this.customWallWidth = feet;
        this.customWidthPct = widthPct;
        this.roomBase = {
          leftPct: clamp(centerX - widthPct / 2, 0, 100 - widthPct),
          topPct: clamp(centerY - heightPct / 2, 0, 100 - heightPct),
          widthPct,
          heightPct,
        };
        this.offset = { x: 0, y: 0 };
        if (this.customScaleInput) this.customScaleInput.value = String(feet);
        if (this.customScaleValue) this.customScaleValue.textContent = this.formatWallWidth(feet);
        this.stage.style.setProperty('--room-width', `${widthPct.toFixed(3)}%`);
        this.applyRoomPlacement();
      }

      /**
       * Position the wall inside the room photo. Same maths as the generator:
       * one reference measurement sets px-per-inch, the wall is centred on
       * anchor.centerX, and its bottom sits `gap` inches above the sofa back
       * (never closer than `topMargin` inches to the top of the photo).
       */
      placeInRoom() {
        const room = this.room;
        if (!room || !this.stage) return;

        if (room.custom) {
          const widthPct = Math.min(this.customWidthPct, this.customMaxWidth());
          const imageAspect = room.size.w / room.size.h;
          const heightPct = (widthPct * imageAspect) / this.wallAspect();
          const maxTop = Math.max(0, 100 - heightPct);

          this.customWidthPct = widthPct;
          this.stage.style.setProperty('--room-image', `url('${room.image}')`);
          this.stage.style.setProperty('--room-ratio', `${room.size.w} / ${room.size.h}`);
          this.stage.style.setProperty('--room-width', `${widthPct.toFixed(3)}%`);
          this.stage.classList.add('is-custom-room');
          this.syncCustomScale();

          this.roomBase = {
            leftPct: (100 - widthPct) / 2,
            topPct: Math.min(12, maxTop),
            widthPct,
            heightPct,
          };
          this.applyRoomPlacement();
          return;
        }

        this.stage.classList.remove('is-custom-room');
        this.syncCustomScale();

        const ppi = room.reference.px / room.reference.inches;
        const wallPxW = this.layout.canvas.w * ppi;
        const wallPxH = this.layout.canvas.h * ppi;
        const left = room.anchor.centerX - wallPxW / 2;
        const topMarginPx = (room.topMargin ?? 6) * ppi;
        const gapPx = (room.gap ?? 8) * ppi;

        let top = room.anchor.sofaBackTopY - gapPx - wallPxH;
        if (top < topMarginPx) top = topMarginPx;

        this.stage.style.setProperty('--room-image', `url('${room.image}')`);
        this.stage.style.setProperty('--room-ratio', `${room.size.w} / ${room.size.h}`);
        this.stage.style.setProperty('--room-width', `${((wallPxW / room.size.w) * 100).toFixed(3)}%`);

        // base = the computed "correct" placement; the drag is a pure offset on top
        this.roomBase = {
          leftPct: (left / room.size.w) * 100,
          topPct: (top / room.size.h) * 100,
          widthPct: (wallPxW / room.size.w) * 100,
          heightPct: (wallPxH / room.size.h) * 100,
        };
        this.applyRoomPlacement();
      }

      /** Base placement + the preview drag offset, clamped to the room photo. */
      applyRoomPlacement() {
        const b = this.roomBase;
        if (!b) return;
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        const x = clamp(b.leftPct + this.offset.x, 0, 100 - b.widthPct);
        const y = clamp(b.topPct + this.offset.y, 0, 100 - b.heightPct);
        this.stage.style.setProperty('--room-left', `${x.toFixed(3)}%`);
        this.stage.style.setProperty('--room-top', `${y.toFixed(3)}%`);
        this.querySelector('[data-reset-position]')?.toggleAttribute('hidden', this.offset.x === 0 && this.offset.y === 0);
      }

      /* ---- preview-only drag to reposition the whole wall ----------------- */

      onPointer(type, event) {
        if (type === 'pointerdown') return this.dragStart(event);
        if (type === 'pointermove') return this.dragMove(event);
        return this.dragEnd(event);
      }

      dragStart(event) {
        if (!this.stage.classList.contains('is-room')) return;
        if (event.button !== 0 || !this.roomBase) return;
        const rect = this.stage.getBoundingClientRect();
        this.dragState = {
          id: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          rectW: rect.width,
          rectH: rect.height,
          startOffset: { ...this.offset },
          moved: false,
        };
      }

      dragMove(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        const dx = event.clientX - d.startX;
        const dy = event.clientY - d.startY;
        if (!d.moved && Math.abs(dx) < 4 && Math.abs(dy) < 4) return;

        if (!d.moved) {
          d.moved = true;
          this.stage.setPointerCapture?.(event.pointerId);
        }
        this.stage.classList.add('is-dragging');
        event.preventDefault();

        const b = this.roomBase;
        const clamp = (v, lo, hi) => Math.min(Math.max(v, lo), Math.max(lo, hi));
        this.offset.x = clamp(
          d.startOffset.x + (dx / d.rectW) * 100,
          -b.leftPct,
          100 - b.widthPct - b.leftPct
        );
        this.offset.y = clamp(
          d.startOffset.y + (dy / d.rectH) * 100,
          -b.topPct,
          100 - b.heightPct - b.topPct
        );
        this.applyRoomPlacement();
      }

      dragEnd(event) {
        const d = this.dragState;
        if (!d || event.pointerId !== d.id) return;
        this.dragState = null;
        this.stage.classList.remove('is-dragging');
        if (d.moved) this.lastDragEnd = Date.now();
      }

      resetPosition() {
        this.offset = { x: 0, y: 0 };
        this.applyRoomPlacement();
      }

      pick(index) {
        this.activeIndex = Number(index);
        this.fileInput.value = '';
        this.fileInput.click();
      }

      setFile(index, file) {
        if (!file || index === null || Number.isNaN(index)) return;
        index = Number(index);

        if (this.files.has(index)) URL.revokeObjectURL(this.files.get(index).preview);
        const preview = URL.createObjectURL(file);
        const entry = { file, preview, url: '', status: this.uploadEndpoint ? 'uploading' : 'ready' };
        this.files.set(index, entry);

        const art = this.wall.querySelector(`[data-art="${index}"]`);
        if (art) {
          art.style.backgroundImage = `url('${preview}')`;
          art.classList.add('is-filled');
        }
        this.wall.querySelector(`[data-frame-index="${index}"]`)?.classList.remove('is-missing');
        this.wall.querySelector(`[data-frame-index="${index}"]`)?.classList.add('is-filled');

        const row = this.controls.querySelector(`[data-row="${index}"]`);
        row?.querySelector(`[data-dropzone="${index}"]`)?.classList.remove('is-missing');
        row?.querySelector(`[data-dropzone="${index}"]`)?.classList.add('is-filled');
        const thumb = row?.querySelector(`[data-thumb="${index}"]`);
        if (thumb) {
          thumb.src = preview;
          thumb.hidden = false;
        }
        const prompt = row?.querySelector(`[data-prompt="${index}"]`);
        if (prompt) prompt.hidden = true;
        const clear = row?.querySelector('[data-clear]');
        if (clear) clear.hidden = false;

        this.updateStatus(index);
        this.hideNotice();

        if (this.uploadEndpoint) this.upload(index, file);
        else this.syncProperties();
      }

      async upload(index, file) {
        const body = new FormData();
        body.append('file', file);
        body.append('frame', String(index + 1));
        body.append('product', this.dataset.product || '');

        try {
          const response = await fetch(`${this.uploadEndpoint}/upload`, { method: 'POST', body });
          const data = await response.json().catch(() => ({}));
          if (!response.ok) throw new Error(data.error || `upload failed (${response.status})`);

          const entry = this.files.get(index);
          if (!entry) return; // removed while the upload was in flight
          entry.url = data.url;
          entry.status = 'ready';
        } catch (error) {
          const entry = this.files.get(index);
          if (!entry) return;
          entry.status = 'error';
          entry.error = error.message || 'upload failed';
          console.error('gallery-wall-customizer: upload failed', error);
        }

        this.updateStatus(index);
        this.syncProperties();
      }

      updateStatus(index) {
        const entry = this.files.get(index);
        const row = this.controls.querySelector(`[data-row="${index}"]`);
        if (!entry || !row) return;

        const zone = row.querySelector(`[data-dropzone="${index}"]`);
        zone?.classList.toggle('is-uploading', entry.status === 'uploading');
        zone?.classList.toggle('is-error', entry.status === 'error');

        const filename = row.querySelector(`[data-filename="${index}"]`);
        if (filename) {
          if (entry.status === 'uploading') filename.textContent = 'Uploading…';
          else if (entry.status === 'error') filename.textContent = `Upload failed — ${entry.error}`;
          else filename.textContent = entry.file.name;
        }
      }

      clear(index) {
        index = Number(index);
        const entry = this.files.get(index);
        if (entry) URL.revokeObjectURL(entry.preview);
        this.files.delete(index);

        const art = this.wall.querySelector(`[data-art="${index}"]`);
        if (art) {
          art.style.backgroundImage = '';
          art.classList.remove('is-filled');
        }
        this.wall.querySelector(`[data-frame-index="${index}"]`)?.classList.remove('is-filled', 'is-missing');

        const row = this.controls.querySelector(`[data-row="${index}"]`);
        row
          ?.querySelector(`[data-dropzone="${index}"]`)
          ?.classList.remove('is-filled', 'is-uploading', 'is-error', 'is-missing');
        const thumb = row?.querySelector(`[data-thumb="${index}"]`);
        if (thumb) {
          thumb.removeAttribute('src');
          thumb.hidden = true;
        }
        const prompt = row?.querySelector(`[data-prompt="${index}"]`);
        if (prompt) prompt.hidden = false;
        const filename = row?.querySelector(`[data-filename="${index}"]`);
        if (filename) filename.textContent = '';
        const clearButton = row?.querySelector('[data-clear]');
        if (clearButton) clearButton.hidden = true;

        this.syncProperties();
      }

      /**
       * Mirror the artwork onto the product form as line-item properties, so
       * the order carries what to print. Only a stored URL is written: a local
       * filename is not an artwork, and writing one would put something that
       * looks like a reference on the order while the image itself never left
       * the browser.
       */
      syncProperties() {
        const form = cartForm();
        if (!form) return;
        if (this.dataset.enableProperties !== 'true' && !this.uploadEndpoint) return;

        const write = (name, value) => {
          let input = form.querySelector(`input[name="${name}"]`);
          if (!value) {
            input?.remove();
            return;
          }
          if (!input) {
            input = document.createElement('input');
            input.type = 'hidden';
            input.name = name;
            form.appendChild(input);
          }
          input.value = value;
        };

        // drop properties for frames that no longer have a photo
        form.querySelectorAll('input[name^="properties[Artwork "]').forEach((input) => {
          const n = Number((input.name.match(/(\d+)/) || [])[1]);
          if (!this.files.has(n - 1)) input.remove();
        });

        for (const [index, entry] of this.files) {
          write(`properties[Artwork ${index + 1}]`, entry.url || '');
        }
      }

      /** The customer's free-text customisation note rides along on the line item. */
      syncNote() {
        const form = cartForm();
        if (!form) return;
        const name = 'properties[Customisation notes]';
        const value = (this.noteEl?.value || '').trim();
        let input = form.querySelector(`input[name="${name}"]`);
        if (!value) {
          input?.remove();
          return;
        }
        if (!input) {
          input = document.createElement('input');
          input.type = 'hidden';
          input.name = name;
          form.appendChild(input);
        }
        input.value = value;
      }

      /** Frame indexes with no stored photo yet. */
      missingFrames() {
        if (!this.requirePhoto) return [];
        const total = this.layout?.frames?.length || 0;
        const missing = [];
        for (let index = 0; index < total; index += 1) {
          if (this.files.get(index)?.status !== 'ready') missing.push(index);
        }
        return missing;
      }

      /**
       * Nothing goes to the cart while a photo is uploading, has failed, or (when
       * the section requires them) is missing. A wall without every frame is an
       * order we cannot print.
       */
      gateSubmit() {
        if (!this.uploadEndpoint) return;
        const form = cartForm();
        if (!form) return;

        form.addEventListener(
          'submit',
          (event) => {
            const entries = [...this.files.values()];
            const uploading = entries.some((e) => e.status === 'uploading');
            const failed = entries.some((e) => e.status === 'error');
            const missing = this.missingFrames();

            if (!uploading && !failed && !missing.length) {
              this.hideNotice();
              return;
            }

            event.preventDefault();
            event.stopImmediatePropagation();

            let notice = '';
            if (uploading) {
              notice = 'Hold on — your photos are still uploading. Give it a moment and try again.';
            } else if (failed) {
              notice = 'Some photos failed to upload. Remove and re-add them, then try again.';
            } else if (missing.length === 1) {
              notice = `Frame ${missing[0] + 1} still needs a photo.`;
            } else {
              notice = `Frames ${missing.map((i) => i + 1).join(', ')} still need a photo.`;
            }

            this.markMissing(uploading || failed ? [] : missing);
            this.showNotice(notice);
            /*
             * The uploader is its own section below the buy button, so the notice
             * above is off screen for anyone who pressed Add to cart up here. Send
             * the same reason to the theme's error region, which sits directly
             * above the button, and say that the frames are further down.
             */
            this.showFormError(
              missing.length && !uploading && !failed
                ? `${notice} Add ${missing.length === 1 ? 'it' : 'them'} below, then try again.`
                : notice
            );
          },
          true
        );
      }

      /** Outline the frames still waiting on a photo. */
      markMissing(indexes) {
        this.wall.querySelectorAll('[data-frame-index].is-missing').forEach((el) => el.classList.remove('is-missing'));
        this.controls.querySelectorAll('[data-dropzone].is-missing').forEach((el) => el.classList.remove('is-missing'));

        indexes.forEach((index) => {
          this.wall.querySelector(`[data-frame-index="${index}"]`)?.classList.add('is-missing');
          this.controls.querySelector(`[data-dropzone="${index}"]`)?.classList.add('is-missing');
        });
      }

      /**
       * Reuse the theme's own error region above the Add to cart button, the one
       * product-form.js drives, so the reason is where the button is.
       */
      showFormError(message) {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(message);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        const slot = wrapper?.querySelector('.product-form__error-message');
        if (!wrapper || !slot) return;
        slot.textContent = message;
        wrapper.hidden = false;
      }

      clearFormError() {
        const productForm = cartForm()?.closest('product-form');
        if (!productForm || productForm.hideErrors) return;

        if (typeof productForm.handleErrorMessage === 'function') {
          productForm.handleErrorMessage(false);
          return;
        }

        const wrapper = productForm.querySelector('.product-form__error-message-wrapper');
        if (wrapper) wrapper.hidden = true;
      }

      showNotice(message) {
        const notice = this.controls.querySelector('[data-notice]');
        if (!notice) return;
        notice.textContent = message;
        notice.hidden = false;
      }

      hideNotice() {
        const notice = this.controls.querySelector('[data-notice]');
        if (notice) notice.hidden = true;
        this.clearFormError();
      }
    }
  );
}
